import React, { useState, useMemo } from 'react';
import CalculatorLayout from '../../components/CalculatorLayout';
import SliderInput, { formatINR } from '../../components/SliderInput';
import { Button } from '../../components/ui/button';

const GST_SLABS = [0, 3, 5, 12, 18, 28];

const GSTCalculator = () => {
  const [amount, setAmount] = useState(1000);
  const [rate, setRate] = useState(18);
  const [mode, setMode] = useState('add');

  const { gst, base, total, cgst, sgst } = useMemo(() => {
    let g, b, t;
    if (mode === 'add') {
      b = amount;
      g = (amount * rate) / 100;
      t = b + g;
    } else {
      t = amount;
      b = (amount * 100) / (100 + rate);
      g = t - b;
    }
    return { gst: g, base: b, total: t, cgst: g / 2, sgst: g / 2 };
  }, [amount, rate, mode]);

  return (
    <CalculatorLayout
      title="GST Calculator"
      subtitle="Add or remove Goods and Services Tax. Splits into CGST and SGST automatically."
      category="Financial"
      description={<>
        <p>GST in India is split equally between Central GST (CGST) and State GST (SGST) for intra-state transactions, or charged as Integrated GST (IGST) for inter-state. Common slabs are 0%, 3%, 5%, 12%, 18%, and 28%.</p>
        <p>Use this calculator to add GST to a base amount, or to extract GST from a tax-inclusive total.</p>
      </>}
      formulaNote="Add GST: Total = Base × (1 + r/100)   •  Remove GST: Base = Total × 100 / (100 + r)"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-5">
          <div className="flex gap-2">
            <Button onClick={() => setMode('add')} variant={mode === 'add' ? 'default' : 'outline'} className={mode === 'add' ? 'bg-saffron-500 hover:bg-saffron-600 text-white' : ''}>+ Add GST</Button>
            <Button onClick={() => setMode('remove')} variant={mode === 'remove' ? 'default' : 'outline'} className={mode === 'remove' ? 'bg-indgreen-500 hover:bg-indgreen-600 text-white' : ''}>− Remove GST</Button>
          </div>

          <SliderInput
            label={mode === 'add' ? 'Base Amount' : 'Total (incl. GST)'}
            unit="₹"
            unitColor="saffron"
            value={amount}
            onChange={setAmount}
            min={1}
            max={1000000}
            step={1}
            format={formatINR}
          />

          <div>
            <label className="text-sm font-medium text-slate-700 mb-2 block">GST Slab</label>
            <div className="flex gap-2 flex-wrap">
              {GST_SLABS.map(s => (
                <button
                  key={s}
                  onClick={() => setRate(s)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium border ${rate === s ? 'bg-indgreen-500 text-white border-indgreen-500' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                >
                  {s}%
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-saffron-50 to-indgreen-50/40 rounded-xl border border-slate-200 p-5 space-y-4">
          <Row label={mode === 'add' ? 'Base Amount' : 'Excl. GST Base'} value={formatINR(base)} />
          <Row label={`GST @ ${rate}%`} value={formatINR(gst)} tone="saffron" big />
          <div className="pl-4 space-y-1 border-l-2 border-saffron-200">
            <SubRow label="CGST (Central)" value={formatINR(cgst)} />
            <SubRow label="SGST (State)" value={formatINR(sgst)} />
          </div>
          <div className="pt-3 border-t border-slate-200">
            <Row label={mode === 'add' ? 'Total (incl. GST)' : 'Total Paid'} value={formatINR(total)} tone="green" big />
          </div>
        </div>
      </div>
    </CalculatorLayout>
  );
};

const Row = ({ label, value, tone, big }) => {
  const c = tone === 'saffron' ? 'text-saffron-700' : tone === 'green' ? 'text-indgreen-700' : 'text-bharat-navy';
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{label}</span>
      <span className={`display-font font-bold ${big ? 'text-2xl md:text-3xl' : 'text-lg'} ${c}`}>{value}</span>
    </div>
  );
};
const SubRow = ({ label, value }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-slate-500">{label}</span>
    <span className="display-font font-semibold text-saffron-700">{value}</span>
  </div>
);

export default GSTCalculator;
